import React from 'react'
import PropTypes from 'prop-types'
import ReactMarkdown from 'react-markdown'
import { cloneDeep } from 'lodash-es'
import { defaultSchema } from 'hast-util-sanitize'
import rehypeRaw from 'rehype-raw'
import rehypeSanitize from 'rehype-sanitize'

import { COLOR } from '@farewill/ui/tokens'
import { Wrapper } from '@farewill/ui'
import styled from 'styled-components'
import DefaultLayout from 'layouts/DefaultLayout'
import { LIVE_PRODUCTS, PRODUCTS } from 'lib/products/constants'
import { StyledRichContentWrapper } from 'views/general/templates/components/RichContent'
// eslint-disable-next-line @typescript-eslint/no-var-requires
const remarkHeadingId = require('remark-heading-id')

const StyledWrapper = styled(Wrapper)`
  ol {
    list-style-type: number;
  }
  ul {
    list-style-type: disc;
  }
  li {
    margin-left: 24px;
  }
  table {
    border-collapse: collapse;
  }
  td {
    border: 1px solid ${COLOR.GREY.DARK};
    padding: 10px;
  }
  th {
    border: 1px solid ${COLOR.GREY.DARK};
    padding: 10px;
    text-align: left;
  }
`

/**
 * Clone the default rehype schema and add `className` to the list of allowed
 * attributes (required for the CookieYes cookie table to work).
 *
 * This should be safe as all policy content comes from the wills API endpoint
 * which serves the content from the `policies` MongoDB collection, which is
 * populated from Markdown files stored in the Wills API repo.
 *
 * Thus, the only way malicious content could make it into this markdown is
 * either:
 * 1. A nefarious actor has access to MongoDB which would allow them to update
 *    `content` field of the policy document stored in the database. If this
 *    happens we've got bigger issues than a potential markdown-induced
 *    vulnerability.
 * 2. A nefarious actor has committed (and merged) changes to the policy
 *    markdown files in Git. If the actor had such access they would likely make
 *    code changes directly to the application code itself, rather than by
 *    trying to embed malicious code in the policy content in Markdown.
 */
const schema = cloneDeep(defaultSchema)
schema.attributes['*'].push('className')

// Create a custom instance of rehype-sanitize with our custom schema.
const sanitizeInstance = rehypeSanitize(schema)

export type TermsProps = {
  pageContext: {
    policy: string
    title: string
    description: string
    compactHeader?: boolean
    funeralsFooter?: boolean
    product: PRODUCTS
  }
}

const Terms = ({ pageContext }: TermsProps) => (
  <DefaultLayout
    title={pageContext.title}
    description={pageContext.description}
    product={pageContext.product}
    headerType={pageContext?.compactHeader ? 'compact' : undefined}
    footerShowFuneralPlansLegalInformation={!!pageContext?.funeralsFooter}
  >
    <StyledWrapper container tag={StyledRichContentWrapper}>
      <ReactMarkdown
        rehypePlugins={
          [rehypeRaw, sanitizeInstance] as ReactMarkdown.PluggableList
        }
        remarkPlugins={[remarkHeadingId]}
      >
        {pageContext.policy}
      </ReactMarkdown>
    </StyledWrapper>
  </DefaultLayout>
)

Terms.propTypes = {
  pageContext: PropTypes.shape({
    policy: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    product: PropTypes.oneOf(LIVE_PRODUCTS),
  }).isRequired,
}

export default Terms
